import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';


import {
  GetApiV5FirmsDashboardWidgetPreferencesSection,
  type GetApiV5FirmsDashboardWidgetPreferences200,
  getGetApiV5FirmsDashboardWidgetPreferencesQueryKey,
  useGetApiV5FirmsDashboardWidgetPreferences,
  usePostApiV5UserJourneySteps,
  usePutApiV5FirmsDashboardWidgetPreferencesWidgetKey,
} from '@liscio/api';
import { useTrackEvent } from '@liscio/common';

import {
  WHAT_TO_DO_TODAY_DASHBOARD_STEPS,
  WHAT_TO_DO_TODAY_EVENTS,
  WHAT_TO_DO_TODAY_SOURCE,
} from './constants';
import {
  buildDashboardActions,
  DASHBOARD_ACTION_IDS,
  isDashboardActionId,
  type DashboardAction,
  type DashboardActionId,
} from './dashboardActions';
import type { SmartTaxOrganizerCompletionResult } from './SmartTaxOrganizerModal/types';
import { buildJourneyStepPayload } from './userJourneySteps';
import {
  MESSAGES_DOMAIN,
  MESSAGES_PATHS,
} from 'modules/messages/route-constants';


const PREFERENCE_PARAMS = {
  section:
    GetApiV5FirmsDashboardWidgetPreferencesSection.what_to_do_today,
};

const DEFAULT_PREFERENCES: GetApiV5FirmsDashboardWidgetPreferences200 = {
  data: DASHBOARD_ACTION_IDS.map((widgetKey) => ({
    widget_key: widgetKey,
    section: PREFERENCE_PARAMS.section,
    visible: true,
  })),
};

export interface SmartTaxDeliveryCompletionResult {
  wfRequestUuid: string;
  rpaDocumentUuid: string | null;
  fileCount: number;
}

export interface UseWhatToDoToday {
  actions: DashboardAction[];
  visibleActions: DashboardAction[];
  isLoadingPreferences: boolean;
  setActionVisibility: (
    actionId: DashboardActionId,
    visible: boolean
  ) => Promise<boolean>;
  savingActionId: DashboardActionId | null;
  preferenceError: string | null;
  clearPreferenceError: () => void;
  isUploadModalOpen: boolean;
  closeUploadModal: () => void;
  isSmartTaxOrganizerModalOpen: boolean;
  closeSmartTaxOrganizerModal: () => void;
  completeSmartTaxOrganizer: (result: SmartTaxOrganizerCompletionResult) => void;
  isSmartTaxDeliveryModalOpen: boolean;
  closeSmartTaxDeliveryModal: () => void;
  completeSmartTaxDelivery: (result: SmartTaxDeliveryCompletionResult) => void;
}

export interface UseWhatToDoTodayOptions {
  autoOpenFileIntelligence?: boolean;
  onAutoOpenConsumed?: () => void;
}

export function useWhatToDoToday({
  autoOpenFileIntelligence = false,
  onAutoOpenConsumed,
}: UseWhatToDoTodayOptions = {}): UseWhatToDoToday {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { trackEvent } = useTrackEvent();
  const preferenceQuery = useGetApiV5FirmsDashboardWidgetPreferences(
    PREFERENCE_PARAMS
  );
  const { mutateAsync: savePreference } =
    usePutApiV5FirmsDashboardWidgetPreferencesWidgetKey();
  const { mutateAsync: recordJourneyStep } = usePostApiV5UserJourneySteps();

  const [savingActionId, setSavingActionId] =
    useState<DashboardActionId | null>(null);
  const [preferenceError, setPreferenceError] = useState<string | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isSmartTaxOrganizerModalOpen, setIsSmartTaxOrganizerModalOpen] =
    useState(false);
  const [isSmartTaxDeliveryModalOpen, setIsSmartTaxDeliveryModalOpen] =
    useState(false);
  const autoOpenConsumedRef = useRef(false);
  const trackStepStarted = useCallback(
    (dashboardStep: string) => {
      trackEvent(WHAT_TO_DO_TODAY_EVENTS.stepStarted, {
        source: WHAT_TO_DO_TODAY_SOURCE,
        dashboard_step: dashboardStep,
      });
    },
    [trackEvent]
  );

  const openUploadModal = useCallback(() => {
    trackStepStarted(WHAT_TO_DO_TODAY_DASHBOARD_STEPS.uploadFile);
    setIsUploadModalOpen(true);
  }, [trackStepStarted]);
  useEffect(() => {
    if (!autoOpenFileIntelligence || autoOpenConsumedRef.current) return;
    autoOpenConsumedRef.current = true;
    openUploadModal();
    onAutoOpenConsumed?.();
  }, [autoOpenFileIntelligence, onAutoOpenConsumed, openUploadModal]);
  const closeUploadModal = useCallback(() => setIsUploadModalOpen(false), []);
  const openSmartTaxOrganizerModal = useCallback(() => {
    trackStepStarted(WHAT_TO_DO_TODAY_DASHBOARD_STEPS.smartTaxOrganizer);
    setIsSmartTaxOrganizerModalOpen(true);
  }, [trackStepStarted]);
  const closeSmartTaxOrganizerModal = useCallback(
    () => setIsSmartTaxOrganizerModalOpen(false),
    []
  );
  const openSmartTaxDeliveryModal = useCallback(() => {
    trackStepStarted(WHAT_TO_DO_TODAY_DASHBOARD_STEPS.smartTaxDelivery);
    setIsSmartTaxDeliveryModalOpen(true);
  }, [trackStepStarted]);
  const closeSmartTaxDeliveryModal = useCallback(
    () => setIsSmartTaxDeliveryModalOpen(false),
    []
  );
  const openMessages = useCallback(() => {
    trackStepStarted(WHAT_TO_DO_TODAY_DASHBOARD_STEPS.sendMessage);
    navigate(`/${MESSAGES_DOMAIN}${MESSAGES_PATHS.new}`);
  }, [navigate, trackStepStarted]);

  const actions = useMemo(
    () =>
      buildDashboardActions({
        onUploadFile: openUploadModal,
        onSmartTaxDelivery: openSmartTaxDeliveryModal,
        onSmartTaxOrganizer: openSmartTaxOrganizerModal,
        onSendMessage: openMessages,
      }),
    [
      openMessages,
      openSmartTaxDeliveryModal,
      openSmartTaxOrganizerModal,
      openUploadModal,
    ]
  );

  const visibleActions = useMemo(() => {
    const visibility = new Map<DashboardActionId, boolean>();
    preferenceQuery.data?.data.forEach((preference) => {
      if (isDashboardActionId(preference.widget_key)) {
        visibility.set(preference.widget_key, preference.visible);
      }
    });

    return actions.filter((action) => visibility.get(action.id) ?? true);
  }, [actions, preferenceQuery.data?.data]);

  const setActionVisibility = useCallback(
    async (actionId: DashboardActionId, visible: boolean) => {
      const queryKey = getGetApiV5FirmsDashboardWidgetPreferencesQueryKey(
        PREFERENCE_PARAMS
      );
      setPreferenceError(null);
      setSavingActionId(actionId);
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<GetApiV5FirmsDashboardWidgetPreferences200>(
        queryKey
      );
      const current = previous ?? DEFAULT_PREFERENCES;
      queryClient.setQueryData<GetApiV5FirmsDashboardWidgetPreferences200>(
        queryKey,
        {
          data: current.data.map((preference) => {
            return preference.widget_key === actionId
              ? { ...preference, visible }
              : preference;
          }),
        }
      );

      try {
        await savePreference({
          widgetKey: actionId,
          data: { section: PREFERENCE_PARAMS.section, visible },
        });
        await queryClient.invalidateQueries({ queryKey });
        return true;
      } catch {
        queryClient.setQueryData(queryKey, previous ?? DEFAULT_PREFERENCES);
        setPreferenceError('Your dashboard preference was not saved. Try again.');
        await queryClient.invalidateQueries({ queryKey });
        return false;
      } finally {
        setSavingActionId(null);
      }
    },
    [queryClient, savePreference]
  );

  const persistActionCompletion = useCallback(
    async (
      actionId: DashboardActionId,
      options: {
        targetType?: 'Contact' | 'Account' | 'Document' | 'WfRequest';
        targetId?: number;
        metadata?: Record<string, unknown>;
      } = {}
    ) => {
      try {
        await recordJourneyStep({
          data: buildJourneyStepPayload({
            actionId,
            targetType: options.targetType,
            targetId: options.targetId,
            metadata: options.metadata,
          }),
        });
      } catch (error) {
        // Journey tracking must not block the completed product action.
        // eslint-disable-next-line no-console
        console.error('Failed to record dashboard journey step', error);
      }
    },
    [recordJourneyStep]
  );

  const completeSmartTaxOrganizer = useCallback(
    (result: SmartTaxOrganizerCompletionResult) => {
      persistActionCompletion('smart_tax_organizer', {
        metadata: {
          rfi_document_uuid: result.rfiDocumentUuid,
          rfi_request_uuid: result.rfiRequestUuid,
          template_uuid: result.templateUuid,
          template_name: result.templateName,
          file_count: result.fileCount,
          coming_from: 'whatToDoToday',
        },
      });
      closeSmartTaxOrganizerModal();
    },
    [closeSmartTaxOrganizerModal, persistActionCompletion]
  );

  const completeSmartTaxDelivery = useCallback(
    (result: SmartTaxDeliveryCompletionResult) => {
      persistActionCompletion('smart_tax_delivery', {
        metadata: {
          wf_request_uuid: result.wfRequestUuid,
          rpa_document_uuid: result.rpaDocumentUuid,
          file_count: result.fileCount,
          coming_from: 'whatToDoToday',
        },
      });
      closeSmartTaxDeliveryModal();
    },
    [closeSmartTaxDeliveryModal, persistActionCompletion]
  );

  return {
    actions,
    visibleActions,
    isLoadingPreferences: preferenceQuery.isLoading,
    setActionVisibility,
    savingActionId,
    preferenceError,
    clearPreferenceError: () => setPreferenceError(null),
    isUploadModalOpen,
    closeUploadModal,
    isSmartTaxOrganizerModalOpen,
    closeSmartTaxOrganizerModal,
    completeSmartTaxOrganizer,
    isSmartTaxDeliveryModalOpen,
    closeSmartTaxDeliveryModal,
    completeSmartTaxDelivery,
  };
}
